/**
 * Renderer for the captured area visualization between centerline and ghost line
 */

import { ANIMATION_CONFIG, COLORS } from '../constants';
import { smoothPoints, createPulse, opacityToHex, createMultiStopGradient } from '../utils/canvasUtils';

export class CapturedAreaRenderer {
  /**
   * Main render function for captured areas
   */
  render(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    ghostLinePoints: number[],
    victoryPhase?: string,
    winner?: "A" | "B",
    transferProgress: number = 0
  ): void {
    const smoothedPoints = smoothPoints(ghostLinePoints);
    const maxDistortion = Math.max(...smoothedPoints.map(Math.abs));

    if (maxDistortion <= ANIMATION_CONFIG.CAPTURED_AREA_MIN_DISTORTION) return;

    // Calculate fade multiplier based on victory phase
    let fadeMultiplier = 1;
    if (victoryPhase && victoryPhase !== "idle") {
      if (victoryPhase === "meter-overdrive") {
        fadeMultiplier = 0.8; // Start fading
      } else if (victoryPhase === "elimination") {
        fadeMultiplier = 0.5; // Fade more
      } else if (victoryPhase === "transfer") {
        fadeMultiplier = Math.max(0.1, 0.3 * (1 - transferProgress)); // Fade based on transfer
      } else if (victoryPhase === "complete") {
        fadeMultiplier = 0.1; // Keep minimal visibility instead of fully fading
      }
    }

    ctx.save();

    const pulseTime = Date.now() / 1000;
    const pulseFactor = createPulse(
      pulseTime,
      ANIMATION_CONFIG.CAPTURED_AREA_PULSE_SPEED,
      ANIMATION_CONFIG.CAPTURED_AREA_PULSE_MIN,
      ANIMATION_CONFIG.CAPTURED_AREA_PULSE_MAX
    ) * fadeMultiplier;

    // Draw both sides
    this.drawSideCapture(ctx, width, height, smoothedPoints, 'left', pulseFactor, pulseTime, fadeMultiplier, victoryPhase, winner);
    this.drawSideCapture(ctx, width, height, smoothedPoints, 'right', pulseFactor, pulseTime, fadeMultiplier, victoryPhase, winner);
    
    // Draw scanlines
    this.drawScanlines(ctx, width, height, smoothedPoints, pulseFactor, pulseTime, fadeMultiplier, victoryPhase, winner);

    ctx.restore();
  }

  /**
   * Draws captured area for one side using clipping
   */
  private drawSideCapture(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    smoothedPoints: number[],
    side: 'left' | 'right',
    pulseFactor: number,
    pulseTime: number,
    fadeMultiplier: number = 1,
    victoryPhase?: string,
    winner?: "A" | "B"
  ): void {
    const centerX = width / 2;
    
    // During victory, use winner's colors for all captured areas
    let colors = side === 'left' ? COLORS.left : COLORS.right;
    if (victoryPhase && victoryPhase !== "idle" && winner) {
      const winnerIsLeft = winner === "A";
      colors = winnerIsLeft ? COLORS.left : COLORS.right;
    }
    
    ctx.save();
    
    // Create clipping path for this side
    ctx.beginPath();
    
    // Start from top center
    ctx.moveTo(centerX, 0);
    
    // Trace along the ghost line, only including areas where this side has captured
    for (let i = 0; i < smoothedPoints.length; i++) {
      const y = (i / (smoothedPoints.length - 1)) * height;
      const waveOffset = Math.sin(pulseTime * ANIMATION_CONFIG.CAPTURED_AREA_WAVE_SPEED + i * 0.2) * 
                        ANIMATION_CONFIG.CAPTURED_AREA_WAVE_AMPLITUDE;
      const ghostX = centerX + smoothedPoints[i] + waveOffset;
      
      // Only include points where this side controls
      if (side === 'left' && smoothedPoints[i] > 0.1) {
        ctx.lineTo(ghostX, y);
      } else if (side === 'right' && smoothedPoints[i] < -0.1) {
        ctx.lineTo(ghostX, y);
      } else {
        // Return to centerline when not controlled by this side
        ctx.lineTo(centerX, y);
      }
    }
    
    // Complete the path back along centerline
    ctx.lineTo(centerX, height);
    ctx.closePath();
    
    // Apply clipping
    ctx.clip();
    
    // Now draw the full gradient - it will only show in clipped area
    const maxDistortion = Math.max(...smoothedPoints.map(Math.abs));
    const intensity = Math.min(maxDistortion / 100, 1) * pulseFactor;
    
    // First, draw the zone gradient (vertical from bottom) that extends into captured area
    const zoneAlpha = 0.15; // Match the zone gradient alpha
    const verticalGradient = ctx.createLinearGradient(0, height, 0, 0);
    verticalGradient.addColorStop(0, colors.glow.replace("0.3", String(zoneAlpha * 1.5)));
    verticalGradient.addColorStop(0.5, colors.glow.replace("0.3", String(zoneAlpha * 0.8)));
    verticalGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = verticalGradient;
    ctx.fillRect(0, 0, width, height);
    
    // Then add the original horizontal gradient on top
    const gradientEnd = side === 'left' 
      ? centerX + width * 0.5  // Extend to right edge
      : centerX - width * 0.5; // Extend to left edge
    
    const gradient = createMultiStopGradient(
      ctx,
      centerX, 0,
      gradientEnd, 0,
      [
        { position: 0, color: colors.glow.replace("0.3", String(0.25 * intensity * fadeMultiplier)) },
        { position: 0.2, color: colors.glow.replace("0.3", String(0.18 * intensity * fadeMultiplier)) },
        { position: 0.5, color: colors.glow.replace("0.3", String(0.12 * intensity * fadeMultiplier)) },
        { position: 0.8, color: colors.glow.replace("0.3", String(0.05 * intensity * fadeMultiplier)) },
        { position: 1, color: "rgba(0, 0, 0, 0)" },
      ]
    );
    
    // Fill the entire canvas - clipping will limit it to captured area
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    ctx.restore();
  }


  /**
   * Draws animated scanlines with clipping
   */
  private drawScanlines(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    smoothedPoints: number[],
    pulseFactor: number,
    pulseTime: number,
    fadeMultiplier: number = 1,
    victoryPhase?: string,
    winner?: "A" | "B"
  ): void {
    const centerX = width / 2;
    const maxDistortion = Math.max(...smoothedPoints.map(Math.abs));
    const intensity = Math.min(maxDistortion / 100, 1) * pulseFactor;
    
    // Draw scanlines for left side
    this.drawSideScanlines(ctx, width, height, smoothedPoints, 'left', intensity, pulseTime, fadeMultiplier, victoryPhase, winner);
    
    // Draw scanlines for right side  
    this.drawSideScanlines(ctx, width, height, smoothedPoints, 'right', intensity, pulseTime, fadeMultiplier, victoryPhase, winner);
  }

  /**
   * Draws scanlines for one side using clipping
   */
  private drawSideScanlines(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    smoothedPoints: number[],
    side: 'left' | 'right',
    intensity: number,
    pulseTime: number,
    fadeMultiplier: number = 1,
    victoryPhase?: string,
    winner?: "A" | "B"
  ): void {
    const centerX = width / 2;
    const colors = side === 'left' ? COLORS.left : COLORS.right;
    
    ctx.save();
    
    // Create the same clipping path as for the gradient
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    
    for (let i = 0; i < smoothedPoints.length; i++) {
      const y = (i / (smoothedPoints.length - 1)) * height;
      const waveOffset = Math.sin(pulseTime * ANIMATION_CONFIG.CAPTURED_AREA_WAVE_SPEED + i * 0.2) * 
                        ANIMATION_CONFIG.CAPTURED_AREA_WAVE_AMPLITUDE;
      const ghostX = centerX + smoothedPoints[i] + waveOffset;
      
      if (side === 'left' && smoothedPoints[i] > 0.1) {
        ctx.lineTo(ghostX, y);
      } else if (side === 'right' && smoothedPoints[i] < -0.1) {
        ctx.lineTo(ghostX, y);
      } else {
        ctx.lineTo(centerX, y);
      }
    }
    
    ctx.lineTo(centerX, height);
    ctx.closePath();
    ctx.clip();
    
    // Draw scanlines across the full width - clipping will limit them
    ctx.globalCompositeOperation = "screen";
    const flowOffset = height - ((Date.now() / ANIMATION_CONFIG.SCANLINE_SPEED) % height);
    
    for (let i = 0; i < ANIMATION_CONFIG.SCANLINE_COUNT; i++) {
      const scanY = (flowOffset + (i * height) / ANIMATION_CONFIG.SCANLINE_COUNT) % height;
      
      const scanGradient = ctx.createLinearGradient(
        centerX,
        scanY - ANIMATION_CONFIG.SCANLINE_GRADIENT_SPREAD,
        centerX,
        scanY + ANIMATION_CONFIG.SCANLINE_GRADIENT_SPREAD
      );
      
      scanGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
      scanGradient.addColorStop(0.5, colors.bright + opacityToHex(0.5 * intensity * fadeMultiplier));
      scanGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      
      ctx.fillStyle = scanGradient;
      // Draw full width - clipping handles the bounds
      ctx.fillRect(
        side === 'left' ? centerX : 0,
        scanY - ANIMATION_CONFIG.SCANLINE_GRADIENT_SPREAD,
        width / 2,
        ANIMATION_CONFIG.SCANLINE_HEIGHT
      );
    }
    
    ctx.restore();
  }
}